export function inferBackdropBounds(definition){
 const bounds=[Infinity,Infinity,-Infinity,-Infinity];
 const rail=definition.player?.controller?.rail?.points;
 for(const p of rail??[]){bounds[0]=Math.min(bounds[0],p[0]-6);bounds[1]=Math.min(bounds[1],p[2]-6);bounds[2]=Math.max(bounds[2],p[0]+6);bounds[3]=Math.max(bounds[3],p[2]+6);}
 for(const e of definition.entities??[]){const b=e.body,s=b?.size;
  if(b?.type!=='static'||!s||s[1]>Math.min(s[0],s[2])*.5)continue;
  for(const instance of e.instances??[e.position??[0,0,0]]){
   const x=instance[0]??0,z=instance[2]??0;
   bounds[0]=Math.min(bounds[0],x-s[0]/2);bounds[1]=Math.min(bounds[1],z-s[2]/2);
   bounds[2]=Math.max(bounds[2],x+s[0]/2);bounds[3]=Math.max(bounds[3],z+s[2]/2);
  }
 }
 return Number.isFinite(bounds[0])?bounds:[-20,-20,20,20];
}
import {createWireLandscape} from './wire-landscape.mjs';
export function inferBackdropFloor(definition){
 let area=-1,height=-.15;
 for(const entity of definition.entities??[]){const b=entity.body,size=b?.size;
  if(b?.type!=='static'||!size||size[1]>Math.min(size[0],size[2])*.5)continue;
  const footprint=size[0]*size[2];if(footprint<=area)continue;
  area=footprint;height=(entity.position?.[1]??0)+size[1]-.15;
 }
 return height;
}
// A single background draw; no volumetric ray marching or offscreen passes.
export function createValorianAtmosphere(T,scene,options={},continuity) {
  const settings=options.atmosphere??{}, base=scene.background;
  // Tiny generated environment map gives standard materials a coherent sky
  // reflection. Three filters it once on upload, never as a per-frame scene pass.
  const upper=new T.Color(settings.skyColor??options.background??'#172235');
  const lower=new T.Color(settings.horizonColor??options.background??'#172235');
  const color=new T.Color(), pixels=new Float32Array(64*32*4);
  for(let y=0;y<32;y++){
    const height=Math.cos(y/31*Math.PI), t=T.MathUtils.smoothstep(height,-.12,.85);
    color.copy(lower).lerp(upper,t).multiplyScalar(height<0?.4:1);
    for(let x=0;x<64;x++){const i=(y*64+x)*4;const longitude=x/64*Math.PI*2;
      const warm=Math.pow(Math.max(0,Math.cos(longitude-.7)),24)*Math.exp(-Math.pow((height-.32)*5,2))*2.2;
      const cool=Math.pow(Math.max(0,Math.cos(longitude-3.7)),12)*Math.exp(-Math.pow((height-.55)*4,2))*.9;
      pixels[i]=color.r+warm+cool*.35;pixels[i+1]=color.g+warm*.78+cool*.7;pixels[i+2]=color.b+warm*.55+cool;pixels[i+3]=1;}
  }
  const environment=new T.DataTexture(pixels,64,32,T.RGBAFormat,T.FloatType);
  environment.mapping=T.EquirectangularReflectionMapping;environment.colorSpace=T.LinearSRGBColorSpace;environment.needsUpdate=true;
  scene.environment=environment;scene.environmentIntensity=.65;
  const owner={};continuity.owner=owner;
  const targetTop=upper.clone(),targetHorizon=lower.clone();
  const prior=continuity.palette;
  const fromTop=prior?.top.clone()??targetTop.clone();
  const fromHorizon=prior?.horizon.clone()??targetHorizon.clone();
  let blendElapsed=prior?0:5;
  const material=new T.ShaderMaterial({
    side:T.BackSide,depthWrite:false,depthTest:false,toneMapped:false,
    uniforms:{sun:{value:new T.Vector3(...(options.lighting?.sunPosition??[10,25,8])).normalize()},time:{value:continuity.time},grainAmount:{value:0},gridY:{value:options.backgroundGridY??-.15},transitionEcho:{value:0},base:{value:base},top:{value:fromTop.clone()},horizon:{value:fromHorizon.clone()},authoredPalette:{value:options.authoredPalette?1:0},retroSun:{value:(settings.retroSun??true)?1:0},stars:{value:(settings.stars??true)?1:0}},
    vertexShader:'varying vec3 direction;void main(){direction=position;vec4 p=projectionMatrix*mat4(mat3(viewMatrix))*vec4(position,1.0);gl_Position=p.xyww;}',
    fragmentShader:`varying vec3 direction;uniform vec3 top,horizon;uniform float time,grainAmount,gridY,transitionEcho,authoredPalette,stars,retroSun;
float grainHash(vec2 p){vec3 q=fract(vec3(p.xyx)*.1031);q+=dot(q,q.yzx+33.33);return fract((q.x+q.y)*q.z);}
float softNoise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.0-2.0*f);return mix(mix(grainHash(i),grainHash(i+vec2(1,0)),f.x),mix(grainHash(i+vec2(0,1)),grainHash(i+vec2(1,1)),f.x),f.y);}
float textureField(vec2 p){return softNoise(p)*.58+softNoise(p*2.07)*.28+softNoise(p*4.13)*.14;}
// Valorian twilight: violet/indigo foundations, rose/amber sunset, restrained cyan.
vec3 spectrum(float h){
 float phase=fract(h)*6.0;float f=fract(phase);f=f*f*(3.0-2.0*f);
 vec3 violet=vec3(.39,.045,.86),indigo=vec3(.075,.11,.55),rose=vec3(.9,.12,.43);
 vec3 amber=vec3(1.0,.38,.12),cyan=vec3(.055,.52,.75);
 if(phase<1.0)return mix(indigo,violet,f);
 if(phase<2.0)return mix(violet,rose,f);
 if(phase<3.0)return mix(rose,amber,f);
 if(phase<4.0)return mix(amber,violet,f);
 if(phase<5.0)return mix(violet,cyan,f);
 return mix(cyan,indigo,f);
}
void main(){vec3 d=normalize(direction);float h=smoothstep(-.35,.85,d.y);
vec3 baseColor=mix(horizon*.055+vec3(.005,.008,.025),top*.035+vec3(.004,.006,.018),h);
vec2 p=vec2(d.x+d.z*.71,d.y-d.z*.43)*5.0;
vec2 drift=vec2(time*.072+sin(time*.031)*.6,-time*.053+sin(time*.047)*.4);
vec2 warp=vec2(textureField(p+drift),textureField(p*1.17-drift*.73+8.3));
float body=textureField(p+warp*3.0+drift*.31);
float detail=textureField(p*3.9+warp*5.0-drift*.6);
float density=smoothstep(.23,.78,body*.7+detail*.3);
// Fine per-fragment stippling replaces the separate particle geometry.
// Slow advection and smooth temporal interpolation avoid frame-random flicker.
vec2 sampleUV=gl_FragCoord.xy+warp*24.0+vec2(time*3.7,-time*2.9);
float epoch=floor(time*.65),blend=fract(time*.65);blend=blend*blend*(3.0-2.0*blend);
float grain=mix(grainHash(floor(sampleUV)+epoch*23.7),grainHash(floor(sampleUV)+(epoch+1.0)*23.7),blend);
float hue=body*1.9+detail*1.1+dot(d,vec3(.37,.21,.43))+time*.021;
vec3 pigment=spectrum(hue);
float hueGrain=mix(.5,grainHash(floor(sampleUV)+41.7),grainAmount);
vec3 grainColor=spectrum(hue+(hueGrain-.5)*.7);
float coverage=mix(.65,smoothstep(.25,.78,grain+density*.37),grainAmount);
vec3 color=baseColor+pigment*(.035+density*.2)*(.3+coverage*1.8);
// Broken fine highlights create grain rather than a smooth cloudy wash.
color+=mix(pigment,grainColor,.65)*mix(.12,pow(grain,7.0),grainAmount)*density*.65;
// Sparse HDR grains supply luminous highlights for the existing bloom pass.
float ember=pow(grain,24.0)*smoothstep(.25,.7,density);
color+=mix(pigment*.04,grainColor*ember*2.2,grainAmount);
color+=pigment*pow(detail,5.0)*.12;
// Back-to-front depth samples: overlapping particulate folds with camera parallax.
// Fixed three layers bound the work; gameplay is still rendered in front.
for(int layer=0;layer<3;layer++){
 float index=float(layer),distance=11.0-index*3.1;
 vec3 point=d*distance+cameraPosition*.007;
 vec3 travel=vec3(time*(.037+index*.021),-time*(.031+index*.013),time*.027);
 point+=travel;
 vec2 coord=vec2(point.x+point.z*.43,point.y-point.z*.61);
 float bend=softNoise(coord*.7+index*9.3);
 float structure=softNoise(coord*1.31+bend*3.2-index*7.1);
 float fold=sin(point.y*1.7+sin(point.x*.8+time*.061)*2.1+bend*5.0);
 float shape=exp(-abs(fold)*mix(2.0,8.0,structure));
 float opening=smoothstep(.25,.65,structure);
 float fleck=mix(.6,grainHash(floor(gl_FragCoord.xy*(.7+index*.19)+coord*13.0)),grainAmount);
 float alpha=shape*opening*(.12+.43*smoothstep(.25,.8,fleck));
 vec3 layerColor=spectrum(structure*1.7+bend*.9+index*.27+time*(.017+index*.007));
 float rim=pow(shape,5.0);
 vec3 luminous=layerColor*(.16+fleck*.45+rim*.3);
 luminous+=spectrum(structure+fleck)*pow(fleck,22.0)*rim*.9;
 color=mix(color,luminous,alpha);
}
// Keep the surroundings subordinate to the playable scene.
// Compress luminous peaks and reserve more color for the upper, distant field.
float luminance=dot(color,vec3(.2126,.7152,.0722));
float distanceLift=smoothstep(.05,.8,d.y);
vec3 subdued=mix(vec3(luminance)*vec3(.72,.82,1.0),color,.55);
subdued*=mix(.12,.14,distanceLift)/(1.0+luminance*3.0);
float sunset=exp(-pow((d.y-.025)*7.0,2.0));
vec3 nightTint=mix(vec3(.01,.002,.026),vec3(.002,.003,.012),h);
vec3 atmosphericTint=nightTint+vec3(.042,.009,.016)*sunset+mix(horizon,top,h)*.003;
color=subdued+atmosphericTint;
// Authored worlds use their own visible palette; legacy worlds keep the exact
// original particulate twilight above. Direction anchoring works in portal views.
if(authoredPalette>.5){
 float skyHeight=smoothstep(-.08,.72,d.y);
 color=mix(horizon,top,skyHeight);
 color*=mix(.68,.86,smoothstep(-.35,.15,d.y));
 color+=horizon*exp(-pow((d.y-.015)*12.0,2.0))*.12;
}
// Three depth planes per cube face: no polar star grid or shared sphere rotation.
// Direction-based face selection covers above, below and behind the playable level.
vec3 ad=abs(d);vec2 plane;float face,forward;
if(ad.x>=ad.y&&ad.x>=ad.z){plane=d.yz/ad.x;face=d.x>0.0?0.0:1.0;forward=ad.x;}
else if(ad.y>=ad.z){plane=d.xz/ad.y;face=d.y>0.0?2.0:3.0;forward=ad.y;}
else{plane=d.xy/ad.z;face=d.z>0.0?4.0:5.0;forward=ad.z;}
for(int layer=0;layer<3;layer++){
 float level=float(layer),scale=22.0+level*17.0;
 // Subtle positional parallax separates near and far fields without moving the camera.
 vec2 parallax=vec2(dot(cameraPosition,vec3(.7,.2,.3)),dot(cameraPosition,vec3(-.2,.8,.4)))*(.003/(1.0+level));
 vec2 starUV=(plane+parallax)*scale;
 vec2 cell=floor(starUV),local=fract(starUV);
 float seed=grainHash(cell+vec2(face*73.1,level*31.7));
 vec2 phase=vec2(seed*91.0,grainHash(cell+face+41.0)*83.0);
 vec2 speed=vec2(.16+seed*.22,.12+grainHash(cell+17.0)*.19);
 vec2 center=.5+.26*sin(time*speed+phase+sin(time*.071+phase.yx)*.6);
 float radius=length(local-center);
 float aa=clamp(length(fwidth(starUV))*.4,.012,.12);
 float size=mix(.022,.075,seed)*(1.0-level*.16);
 float core=1.0-smoothstep(size,size+aa,radius);
 float halo=exp(-radius*17.0)*.17*(1.0-level*.25);
 float visible=step(.85+level*.025,seed);
 float twinkle=.64+.36*sin(time*(.6+seed*.9)+seed*150.0+level);
 vec3 starlight=mix(vec3(.53,.69,1.0),vec3(1.0,.79,.64),grainHash(cell+19.7));
 color+=starlight*(core+halo)*visible*twinkle*(1.05-level*.18)*(authoredPalette>.5?stars:1.0);
}
// A short, occasional meteor; direction changes per interval, never gameplay-driven.
float meteorEpoch=floor(time/19.0),meteorAge=mod(time,19.0);
float meteorSeed=grainHash(vec2(meteorEpoch,73.0));
vec2 meteorStart=vec2(mix(-.85,.5,meteorSeed),.38+grainHash(vec2(meteorEpoch,91.0))*.45);
vec2 meteorVelocity=vec2(.3,-.15);
vec2 meteorHead=meteorStart+meteorVelocity*meteorAge;
vec2 meteorTail=meteorHead-meteorVelocity*.48;
vec2 meteorUV=d.xy/max(-d.z,.001);
vec2 meteorLine=meteorHead-meteorTail;
float meteorAlong=clamp(dot(meteorUV-meteorTail,meteorLine)/dot(meteorLine,meteorLine),0.0,1.0);
float meteorDistance=length(meteorUV-mix(meteorTail,meteorHead,meteorAlong));
float meteorAA=clamp(length(fwidth(meteorUV)),.0006,.006);
float meteorGlow=exp(-meteorDistance*230.0)*.12+(1.0-smoothstep(.0007,.0007+meteorAA,meteorDistance));
float meteorLife=smoothstep(.0,.2,meteorAge)*(1.0-smoothstep(1.1,1.8,meteorAge));
color+=vec3(.48,.7,1.0)*meteorGlow*pow(meteorAlong,2.0)*meteorLife*step(.2,-d.z)*.55*(authoredPalette>.5?stars:1.0);
if(retroSun>.5){
// A distant retro sun in the course-forward (-Z) direction.
// Direction anchoring keeps it on the horizon regardless of player translation.
vec3 sunAxis=normalize(vec3(0.0,.105,-1.0));
vec3 sunRight=vec3(1.0,0.0,0.0),sunUp=normalize(cross(sunRight,sunAxis));
vec2 sunUV=vec2(dot(d,sunRight),dot(d,sunUp));
float sunRadius=.19,radius=length(sunUV);
float sunAA=max(fwidth(radius),.0004);
float disc=(1.0-smoothstep(sunRadius-sunAA,sunRadius+sunAA,radius))*step(0.0,dot(d,sunAxis));
float height=sunUV.y/sunRadius;
float stripeCoord=(height+1.0)*7.0-time*.055+.035*sin(sunUV.x*18.0+time*.31);
float stripeAA=min(.15,fwidth(stripeCoord));
float stripe=smoothstep(.22-stripeAA,.22+stripeAA,fract(stripeCoord));
float bands=mix(stripe,1.0,smoothstep(-.1,.42,height));
float breath=.96+.04*sin(time*.43)+.025*sin(time*.19+1.7);
vec3 sunColor=mix(vec3(.95,.035,.24),vec3(1.0,.53,.13),smoothstep(-1.0,1.0,height+.045*sin(time*.23)));
float outward=max(0.0,radius-sunRadius);
float facing=step(0.0,dot(d,sunAxis));
// Bounded analytic bloom keeps the glow available in XR without another pass.
float sunHalo=(exp(-outward*16.0)*.11+exp(-outward*65.0)*.17)*facing*breath;
color+=mix(vec3(.7,.075,.26),vec3(1.0,.34,.12),smoothstep(-.5,.8,height))*sunHalo;
color=mix(color,sunColor*.72*breath,disc*bands);
float rim=exp(-abs(radius-sunRadius)*170.0)*facing;
color+=vec3(1.0,.29,.12)*rim*.22*breath;
}

color+=spectrum(dot(d,vec3(.23,.31,.19))+time*.02)*transitionEcho*.024;
gl_FragColor=vec4(color,1.0);
#include <colorspace_fragment>
}`,

  });
  const paletteState={top:material.uniforms.top.value,horizon:material.uniforms.horizon.value};
  const sky=new T.Mesh(new T.SphereGeometry(1,24,12),material);
  const landscape=settings.wireLandscape===false?{update(){},dispose(){}}:createWireLandscape(T,scene,{floor:options.backgroundGridY??-.15,bounds:options.backgroundGridBounds});
  sky.frustumCulled=false;sky.renderOrder=-1000;scene.add(sky);
  if(settings.fogFar!==undefined)scene.fog=new T.Fog(settings.horizonColor??options.background??'#172235',settings.fogNear??settings.fogFar*.4,settings.fogFar);
  return {sky,update(dt){if(Number.isFinite(dt)&&dt>0){
      const step=Math.min(dt,.1);material.uniforms.time.value+=step;blendElapsed=Math.min(5,blendElapsed+step);
      const x=blendElapsed/5,eased=x*x*(3-2*x);
      const echo=prior?Math.sin(x*Math.PI):0;material.uniforms.transitionEcho.value=echo;landscape.update(material.uniforms.time.value,echo);
      material.uniforms.top.value.copy(fromTop).lerp(targetTop,eased);
      material.uniforms.horizon.value.copy(fromHorizon).lerp(targetHorizon,eased);
      if(continuity.owner===owner){continuity.time=material.uniforms.time.value;continuity.palette=paletteState;}
    }},dispose(){landscape.dispose();sky.removeFromParent();sky.geometry.dispose();material.dispose();environment.dispose();scene.environment=null;scene.fog=null;}};
}

